/** What the run page shows when the agent could not confirm that a run's job stopped. */
export interface UnconfirmedTermination {
  message: string;
  remedy: string | null;
}

/** The 409 refusal with `reason`, read as the message and remedy the run page shows, or null. */
const readTerminateRefusal = (
  error: unknown,
  reason: string
): UnconfirmedTermination | null => {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | undefined
  )?.response;
  if (409 !== response?.status) return null;
  const data = response.data as
    | { message?: unknown; reason?: unknown; remedy?: unknown }
    | undefined;
  if (reason !== data?.reason) return null;
  return {
    message: typeof data.message === "string" ? data.message : "",
    remedy: typeof data.remedy === "string" ? data.remedy : null,
  };
};

/**
 * The backend refuses to terminate an agent run whose job it cannot confirm stopped (the agent is
 * offline, or the attempt never reported a job id) with a 409 whose reason is
 * `agent_termination_unconfirmed`. The user can then terminate without confirmation, which is the
 * only way such a run ever becomes rerunnable. Every other error returns null and keeps the
 * ordinary failure toast.
 */
export const readUnconfirmedTermination = (
  error: unknown
): UnconfirmedTermination | null =>
  readTerminateRefusal(error, "agent_termination_unconfirmed");

/**
 * #974: the scheduler accepted the kill, but the job was still stopping when the backend last checked
 * (`termination_pending`). Nothing failed: the run shows Terminated once the job has stopped, so the
 * run page says so instead of showing a failure.
 */
export const readPendingTermination = (
  error: unknown
): UnconfirmedTermination | null =>
  readTerminateRefusal(error, "termination_pending");
